import { getPermissions, hasPermission } from "../../utilis/permissionStorage";
import React, { useState, useEffect } from "react";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import * as FaIcons from "react-icons/fa";
import Swal from "sweetalert2";
import "./leavePolicies.css";

function LeaveAssignment() {
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [users, setUsers] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState({});
  const [loading, setLoading] = useState(false);
  const [branchLeavePolicies, setBranchLeavePolicies] = useState([]);
  const [assignedLeaves, setAssignedLeaves] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [modalSkeleton, setModalSkeleton] = useState(false);
  const [permissions, setPermissions] = useState({});
  const [manageModal, setManageModal] = useState(false);
  const [manageModalSkeleton, setManageModalSkeleton] = useState(false);
  const [manageLoading, setManageLoading] = useState(false);
  const [activeEntitlements, setActiveEntitlements] = useState([]);
  const [historyEntitlements, setHistoryEntitlements] = useState([]);
  const [manageTab, setManageTab] = useState("active");
  const [editRow, setEditRow] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [branchFilter, setBranchFilter] = useState("all");
  const [viewByBranch, setViewByBranch] = useState(true);
  const [collapsedBranches, setCollapsedBranches] = useState({});

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("en-GB");
  };

  const isInCurrentPeriod = (row) => {
    if (row?.is_current_period !== undefined) return row.is_current_period;
    if (!row?.period_start || !row?.period_end) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(row.period_start);
    const end = new Date(row.period_end);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return start <= today && end >= today;
  };

  const groupHistoryByPeriod = (items) => {
    const groups = new Map();

    items.forEach((item) => {
      const archivedKey = item.archived_at
        ? new Date(item.archived_at).toISOString().slice(0, 19)
        : "legacy";
      const key = `${item.period_start}|${item.period_end}|${archivedKey}`;

      if (!groups.has(key)) {
        groups.set(key, {
          period_start: item.period_start,
          period_end: item.period_end,
          archived_at: item.archived_at,
          items: [],
        });
      }
      groups.get(key).items.push(item);
    });

    return Array.from(groups.values()).sort(
      (a, b) =>
        new Date(b.archived_at || b.period_end) - new Date(a.archived_at || a.period_end)
    );
  };

  const historyGroups = groupHistoryByPeriod(historyEntitlements);

  const getPeriodEnd = (startDateValue) => {
    if (!startDateValue) return "";
    const start = new Date(startDateValue);
    const end = new Date(start);
    end.setFullYear(end.getFullYear() + 1);
    end.setDate(end.getDate() - 1);
    return end.toISOString().split("T")[0];
  };

  const skeletonStyle = (width, height = 18) => ({
    width: `${width}px`,
    height: `${height}px`,
  });

  useEffect(() => {
    const pers = getPermissions();
    setPermissions(pers || {});
  }, []);

  useEffect(() => {
    eligibleUsers();
  }, []);

  const eligibleUsers = async () => {
    setShowSkeleton(true);
    try {
      const response = await apiUrl.get("/leave-eligible-users");
      if (response.status) {
        setUsers(response?.data?.data || []);
      }
    } catch (error) {
      console.log("Error fetching users:", error);
    } finally {
      setShowSkeleton(false);
    }
  };

  const uniqueBranches = [
    ...new Set(users.map((u) => u.branch_name).filter(Boolean)),
  ].sort();

  const filteredUsers = users.filter((user) => {
    const matchesBranch =
      branchFilter === "all" || user.branch_name === branchFilter;
    if (!matchesBranch) return false;
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      user?.name?.toLowerCase().includes(term) ||
      user?.email?.toLowerCase().includes(term) ||
      user?.branch_name?.toLowerCase().includes(term)
    );
  });

  const usersByBranch = filteredUsers.reduce((acc, user) => {
    const key = user.branch_name || "Unknown Branch";
    if (!acc[key]) acc[key] = [];
    acc[key].push(user);
    return acc;
  }, {});

  const toggleBranch = (branch) => {
    setCollapsedBranches((prev) => ({ ...prev, [branch]: !prev[branch] }));
  };

  const loadManageData = async (userId) => {
    const [activeResult, allResult] = await Promise.allSettled([
      apiUrl.get(`/leave-entitlements/user/${userId}/active`),
      apiUrl.get(`/user/leave-entitlements/${userId}`),
    ]);

    const active =
      activeResult.status === "fulfilled"
        ? activeResult.value?.data?.data || []
        : [];
    const all =
      allResult.status === "fulfilled"
        ? allResult.value?.data?.data || []
        : [];

    setActiveEntitlements(active);
    setHistoryEntitlements(
      all.filter((e) => e.is_active === false || e.status === "archived")
    );
  };

  const handleOpenManageModal = async (user) => {
    setManageModal(true);
    setSelectedUser(user);
    setManageModalSkeleton(true);
    setManageTab("active");
    setEditRow(null);
    setEditValue("");
    try {
      await loadManageData(user.id);
    } catch (error) {
      console.log(error);
      Swal.fire({
        icon: "error",
        title: "Failed to load entitlements",
        toast: true,
        position: "top-end",
        timer: 3000,
        showConfirmButton: false,
      });
    } finally {
      setManageModalSkeleton(false);
    }
  };

  const handleSaveUsedDays = async (rowId) => {
    const val = parseFloat(editValue);
    if (Number.isNaN(val) || val < 0) {
      Swal.fire({
        icon: "warning",
        title: "Enter a valid number of used days",
        toast: true,
        position: "top-end",
        timer: 2500,
        showConfirmButton: false,
      });
      return;
    }

    setManageLoading(true);
    try {
      await apiUrl.put(`/user/leave-entitlements/update/${rowId}`, {
        used_days: editValue,
      });
      await loadManageData(selectedUser.id);
      setEditRow(null);
      setEditValue("");
      Swal.fire({
        icon: "success",
        title: "Used days updated",
        toast: true,
        position: "top-end",
        timer: 2500,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: err.response?.data?.message || "Update failed",
        toast: true,
        position: "top-end",
        timer: 3000,
        showConfirmButton: false,
      });
    } finally {
      setManageLoading(false);
    }
  };

  const openAssignModal = async (user) => {
    setOpenModal(true);
    setSelectedUser(user);
    setModalSkeleton(true);
    setStartDate(new Date().toISOString().split("T")[0]);
    setBranchLeavePolicies([]);
    setAssignedLeaves([]);

    const branchId = user?.branch_id;
    if (!branchId || branchId === "N/A") {
      setModalSkeleton(false);
      return;
    }

    try {
      const [policiesResult, assignedResult] = await Promise.allSettled([
        apiUrl.get(`/leave-policies/branch/${branchId}`),
        apiUrl.get(`/leave-entitlements/user/${user.id}/active`),
      ]);

      if (policiesResult.status === "fulfilled") {
        const policiesRes = policiesResult.value;
        if (policiesRes?.data?.status !== false) {
          setBranchLeavePolicies(policiesRes?.data?.data || []);
        }
      } else {
        console.log("Error loading branch policies:", policiesResult.reason);
      }

      if (assignedResult.status === "fulfilled") {
        const assignedRes = assignedResult.value;
        if (assignedRes?.data?.status !== false) {
          setAssignedLeaves(assignedRes?.data?.data || []);
        }
      } else {
        console.log("Error loading active entitlements:", assignedResult.reason);
      }
    } catch (error) {
      console.log("Error loading modal data:", error);
    } finally {
      setModalSkeleton(false);
    }
  };

  const hasActiveAssignment = assignedLeaves.length > 0;

  const handleAssignedLeaveToUser = async () => {
    if (!selectedUser.id || !startDate) {
      Swal.fire({
        icon: "warning",
        title: "Start date is required",
        toast: true,
        position: "top-end",
        timer: 2500,
        showConfirmButton: false,
      });
      return;
    }

    if (hasActiveAssignment) {
      const usedSummary = assignedLeaves
        .map(
          (e) =>
            `${e.leave_type?.name || e.leave_type_name}: ${e.used_days || 0}/${e.total_days || e.max_days} used`
        )
        .join("\n");

      const confirm = await Swal.fire({
        icon: "question",
        title: "Reassign leave balance?",
        html: `<p class="text-start small mb-2">Current active entitlements will be moved to <strong>history</strong> with used/remaining days preserved.</p>
               <pre class="text-start small bg-light p-2 rounded" style="white-space:pre-wrap">${usedSummary || "Active records on file"}</pre>
               <p class="text-start small mb-0">A new balance will start from <strong>${formatDate(startDate)}</strong> using branch policies.</p>`,
        showCancelButton: true,
        confirmButtonText: "Yes, reassign",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#0d6efd",
      });

      if (!confirm.isConfirmed) return;
    }

    setLoading(true);
    try {
      const response = await apiUrl.post("/assign-leave-to-user", {
        user_id: selectedUser.id,
        entitlements: branchLeavePolicies.map((policy) => ({
          leave_type_id: policy.leave_type_id,
          max_days: policy.max_days,
        })),
        period_start: startDate,
        reassign: hasActiveAssignment,
        archive_previous: hasActiveAssignment,
      });

      Swal.fire({
        icon: "success",
        title: response?.data?.message || (hasActiveAssignment ? "Leave reassigned" : "Leave assigned"),
        toast: true,
        position: "top-end",
        timer: 3000,
        showConfirmButton: false,
      });

      setOpenModal(false);
      if (manageModal) {
        setManageTab("active");
        await loadManageData(selectedUser.id);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: error.response?.data?.message || "Assignment failed",
        toast: true,
        position: "top-end",
        timer: 3500,
        showConfirmButton: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const SkeletonRow = () => (
    <tr>
      <td><div className="lp-skeleton" style={skeletonStyle(140)} /></td>
      <td><div className="lp-skeleton" style={skeletonStyle(90)} /></td>
      <td><div className="lp-skeleton" style={skeletonStyle(80)} /></td>
      <td className="text-center"><div className="lp-skeleton mx-auto" style={skeletonStyle(70)} /></td>
      <td className="text-center"><div className="lp-skeleton mx-auto" style={skeletonStyle(70)} /></td>
    </tr>
  );

  const ModalSkeleton = () => (
    <div className="d-flex flex-column gap-3">
      <div className="lp-skeleton" style={skeletonStyle(280, 72)} />
      <div className="lp-skeleton" style={skeletonStyle("100%", 100)} />
      <div className="lp-skeleton" style={skeletonStyle("100%", 160)} />
    </div>
  );

  const renderUserRow = (user) => (
    <tr key={user.id}>
      <td>
        <div className="d-flex align-items-center gap-2">
          <div className="lp-user-avatar">{getInitials(user?.name)}</div>
          <div>
            <div className="fw-semibold">{user?.name}</div>
            <div className="text-muted small">{user?.email}</div>
          </div>
        </div>
      </td>
      <td>
        <span className="badge lp-badge-soft-secondary">{user?.branch_name}</span>
      </td>
      <td>{formatDate(user?.date_of_joining)}</td>
      <td className="text-center">
        <div className="d-flex justify-content-center gap-2">
          <button
            type="button"
            className="lp-user-action-btn assign"
            onClick={() => openAssignModal(user)}
            title="Assign branch leave policies"
          >
            <FaIcons.FaPlusCircle />
            Assign
          </button>
          <button
            type="button"
            className="lp-user-action-btn manage"
            onClick={() => handleOpenManageModal(user)}
            title="View current & history"
          >
            <FaIcons.FaClipboardList />
            Manage
          </button>
        </div>
      </td>
    </tr>
  );

  const renderEntitlementRow = (row, isHistory = false) => (
    <tr key={row.id} className={isHistory ? "lp-history-row" : ""}>
      <td>
        <div className="fw-semibold">{row?.leave_type?.name || row?.leave_type_name}</div>
        <div className="text-muted small">({row?.leave_type?.code || "—"})</div>
      </td>
      <td className="text-center">
        <span className="lp-days-badge">{row.total_days}</span>
      </td>
      <td className="text-center">
        {!isHistory && editRow === row.id ? (
          <input
            type="number"
            className="form-control form-control-sm text-center mx-auto"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            min="0"
            max={row.total_days}
            style={{ width: "72px" }}
          />
        ) : (
          <span className="fw-medium">{row.used_days}</span>
        )}
      </td>
      <td className="text-center">
        <span
          className={`badge ${
            row.remaining_days > 0 ? "lp-badge-soft-success" : "lp-badge-soft-danger"
          }`}
        >
          {row.remaining_days}
        </span>
      </td>
      <td className="text-center">
        <small className="text-muted d-block">
          {formatDate(row.period_start)} – {formatDate(row.period_end)}
        </small>
        {!isHistory && !isInCurrentPeriod(row) && (
          <small className="badge lp-badge-soft-warning mt-1">Starts {formatDate(row.period_start)}</small>
        )}
        {isHistory && row.archived_at && (
          <small className="text-muted d-block">Moved to history {formatDate(row.archived_at)}</small>
        )}
      </td>
      <td className="text-center">
        {isHistory ? (
          <span className="badge lp-badge-soft-secondary">Archived</span>
        ) : editRow === row.id ? (
          <div className="lp-action-btn-group justify-content-center">
            <button
              className="btn btn-sm btn-success"
              onClick={() => handleSaveUsedDays(row.id)}
              disabled={manageLoading}
            >
              {manageLoading ? (
                <span className="spinner-border spinner-border-sm" />
              ) : (
                <FaIcons.FaCheck />
              )}
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => {
                setEditRow(null);
                setEditValue("");
              }}
            >
              <FaIcons.FaTimes />
            </button>
          </div>
        ) : (
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => {
              setEditRow(row.id);
              setEditValue(row.used_days);
            }}
            title="Edit used days"
          >
            <FaIcons.FaPencilAlt />
          </button>
        )}
      </td>
    </tr>
  );

  if (permissions["manage_policies"] !== 1) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning">
          You don't have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <>
      <div>
        {!showSkeleton && users.length > 0 && (
          <div className="row g-3 mb-4">
            <div className="col-sm-4">
              <div className="lp-stat-card d-flex align-items-center gap-3">
                <div className="lp-stat-icon bg-primary bg-opacity-10 text-primary">
                  <FaIcons.FaUsers />
                </div>
                <div>
                  <div className="lp-stat-label">Total Users</div>
                  <div className="lp-stat-value">{users.length}</div>
                </div>
              </div>
            </div>
            <div className="col-sm-4">
              <div className="lp-stat-card d-flex align-items-center gap-3">
                <div className="lp-stat-icon bg-info bg-opacity-10 text-info">
                  <FaIcons.FaBuilding />
                </div>
                <div>
                  <div className="lp-stat-label">Branches</div>
                  <div className="lp-stat-value">{uniqueBranches.length}</div>
                </div>
              </div>
            </div>
            <div className="col-sm-4">
              <div className="lp-stat-card d-flex align-items-center gap-3">
                <div className="lp-stat-icon bg-success bg-opacity-10 text-success">
                  <FaIcons.FaUserCheck />
                </div>
                <div>
                  <div className="lp-stat-label">Shown</div>
                  <div className="lp-stat-value">{filteredUsers.length}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="lp-section-card mb-4">
          <div className="lp-section-header">
            <h5 className="fw-bold mb-1 d-flex align-items-center gap-2">
              <FaIcons.FaUserCheck className="text-primary" />
              User Leave Assignment
            </h5>
            <p className="text-muted small mb-0">
              Assign branch leave policies to employees and manage current & historical balances
            </p>
          </div>
        </div>

        <div className="lp-section-card">
          <div className="lp-section-header">
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
              <div>
                <h6 className="fw-semibold mb-0">Employees by Branch</h6>
                <p className="text-muted small mb-0">
                  {filteredUsers.length} user{filteredUsers.length !== 1 ? "s" : ""}
                </p>
              </div>
              <div className="lp-view-toggle">
                <button
                  type="button"
                  className={viewByBranch ? "active" : ""}
                  onClick={() => setViewByBranch(true)}
                >
                  By Branch
                </button>
                <button
                  type="button"
                  className={!viewByBranch ? "active" : ""}
                  onClick={() => setViewByBranch(false)}
                >
                  All List
                </button>
              </div>
            </div>

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
              {uniqueBranches.length > 0 && (
                <div className="lp-branch-filter">
                  <button
                    type="button"
                    className={`lp-branch-chip ${branchFilter === "all" ? "active" : ""}`}
                    onClick={() => setBranchFilter("all")}
                  >
                    All Branches
                  </button>
                  {uniqueBranches.map((branch) => (
                    <button
                      key={branch}
                      type="button"
                      className={`lp-branch-chip ${branchFilter === branch ? "active" : ""}`}
                      onClick={() => setBranchFilter(branch)}
                    >
                      {branch}
                    </button>
                  ))}
                </div>
              )}
              <div className="input-group ms-auto" style={{ maxWidth: 260 }}>
                <span className="input-group-text bg-white border-end-0">
                  <FaIcons.FaSearch className="text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0 ps-0"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="p-3" style={{ background: "#f8fafc" }}>
            {showSkeleton ? (
              <div className="table-responsive bg-white rounded">
                <table className="table table-hover mb-0">
                  <tbody>
                    {Array(6).fill(0).map((_, i) => (
                      <SkeletonRow key={i} />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="lp-empty-state py-4">
                <div className="empty-icon">
                  <FaIcons.FaUsers size={24} />
                </div>
                <h6 className="fw-semibold">No users found</h6>
                <p className="text-muted small mb-0">Try another branch or search term.</p>
              </div>
            ) : viewByBranch ? (
              Object.entries(usersByBranch)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([branch, branchUsers]) => (
                  <div key={branch} className="lp-branch-group lp-branch-user-group">
                    <div
                      className="lp-branch-group-header"
                      onClick={() => toggleBranch(branch)}
                    >
                      <div className="lp-branch-group-title">
                        <span className="lp-branch-group-icon">
                          <FaIcons.FaBuilding />
                        </span>
                        <div>
                          <div>{branch}</div>
                          <span className="lp-branch-group-count">
                            {branchUsers.length} employee{branchUsers.length !== 1 ? "s" : ""}
                          </span>
                        </div>
                      </div>
                      <FaIcons.FaChevronDown
                        style={{
                          transform: collapsedBranches[branch] ? "rotate(-90deg)" : "rotate(0deg)",
                          transition: "transform 0.2s",
                          color: "#94a3b8",
                        }}
                      />
                    </div>
                    {!collapsedBranches[branch] && (
                      <div className="table-responsive bg-white">
                        <table className="table table-hover align-middle mb-0">
                          <thead>
                            <tr>
                              <th>User</th>
                              <th>Branch</th>
                              <th>Joining</th>
                              <th className="text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody>{branchUsers.map(renderUserRow)}</tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))
            ) : (
              <div className="table-responsive bg-white rounded">
                <table className="table table-hover align-middle mb-0">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Branch</th>
                      <th>Joining</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>{filteredUsers.map(renderUserRow)}</tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {openModal && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.45)", zIndex: 1040 }}
            onClick={() => !loading && setOpenModal(false)}
          />
          <div className="modal fade show d-block" tabIndex={-1} style={{ zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered modal-lg" style={{ maxWidth: 900 }}>
              <div className="modal-content border-0 rounded-4 shadow overflow-hidden">
                <div className="modal-header" style={{ background: "#f7f8fa", borderBottom: "1px solid #eef2f6" }}>
                  <div>
                    <h5 className="modal-title fw-bold mb-0">
                      {hasActiveAssignment ? "Reassign Leave Balance" : "Assign Leave Balance"}
                    </h5>
                    <small className="text-muted">Based on branch policies</small>
                  </div>
                  <button type="button" className="btn-close" onClick={() => !loading && setOpenModal(false)} />
                </div>

                <div className="modal-body py-3 px-4">
                  {modalSkeleton ? (
                    <ModalSkeleton />
                  ) : (
                    <div className="d-flex flex-column gap-3">
                      <div className="lp-modal-user-info">
                        <div className="d-flex align-items-center gap-3">
                          <div className="lp-user-avatar">{getInitials(selectedUser?.name)}</div>
                          <div>
                            <h6 className="fw-bold mb-0">{selectedUser?.name}</h6>
                            <small className="text-muted">{selectedUser?.email}</small>
                          </div>
                          <span className="badge lp-badge-soft-secondary ms-auto">
                            <FaIcons.FaBuilding className="me-1" />
                            {selectedUser?.branch_name}
                          </span>
                        </div>
                      </div>

                      {hasActiveAssignment && (
                        <div className="lp-reassign-notice">
                          <FaIcons.FaInfoCircle className="me-2" />
                          This user already has active leave. Reassigning will archive the current
                          period (keeping used/total records) and create a new active balance.
                        </div>
                      )}

                      <div className="lp-section-card">
                        <div className="lp-section-header py-3">
                          <label className="form-label fw-semibold mb-1">Period Start Date</label>
                          <input
                            type="date"
                            className="form-control"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            min="2000-01-01"
                            max="2100-12-31"
                          />
                        </div>
                      </div>

                      {hasActiveAssignment && (
                        <div className="lp-section-card lp-table-card">
                          <div className="lp-section-header py-2">
                            <h6 className="fw-semibold mb-0 small">
                              Current Active (will move to history)
                            </h6>
                            {assignedLeaves[0]?.period_start && (
                              <small className="text-muted">
                                Period: {formatDate(assignedLeaves[0].period_start)} –{" "}
                                {formatDate(assignedLeaves[0].period_end)}
                              </small>
                            )}
                          </div>
                          <div className="table-responsive">
                            <table className="table table-sm mb-0">
                              <thead>
                                <tr>
                                  <th>Leave Type</th>
                                  <th className="text-center">Total</th>
                                  <th className="text-center">Used</th>
                                  <th className="text-center">Remaining</th>
                                </tr>
                              </thead>
                              <tbody>
                                {assignedLeaves.map((e) => (
                                  <tr key={e.id}>
                                    <td>{e.leave_type?.name || e.leave_type_name}</td>
                                    <td className="text-center">{e.total_days ?? e.max_days}</td>
                                    <td className="text-center">{e.used_days ?? 0}</td>
                                    <td className="text-center">{e.remaining_days ?? "—"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {branchLeavePolicies.length === 0 ? (
                        <div className="lp-empty-state py-3">
                          <div className="empty-icon">
                            <FaIcons.FaExclamationTriangle size={22} />
                          </div>
                          <h6 className="fw-semibold">No branch policies</h6>
                          <p className="text-muted small mb-0">
                            {selectedUser?.branch_id
                              ? `Add active leave allocations for ${selectedUser?.branch_name} (branch #${selectedUser?.branch_id}) in the Leave Allocations tab.`
                              : "This user has no branch assigned. Set their branch first, then configure leave allocations."}
                          </p>
                        </div>
                      ) : (
                        <div className="lp-section-card lp-table-card">
                          <div className="lp-section-header py-2">
                            <h6 className="fw-semibold mb-0 small">New active balance</h6>
                            <small className="text-muted">
                              Period: {formatDate(startDate)} – {formatDate(getPeriodEnd(startDate))}
                            </small>
                          </div>
                          <div className="table-responsive">
                            <table className="table table-sm table-hover mb-0">
                              <thead>
                                <tr>
                                  <th>Leave Type</th>
                                  <th className="text-center">Days</th>
                                </tr>
                              </thead>
                              <tbody>
                                {branchLeavePolicies.map((policy) => (
                                  <tr key={policy.id}>
                                    <td>
                                      <div className="fw-semibold">{policy.leave_type_name}</div>
                                      <small className="text-muted">({policy.leave_type_code})</small>
                                    </td>
                                    <td className="text-center">
                                      <span className="lp-days-badge">{policy.max_days} days</span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="modal-footer" style={{ background: "#f7f8fa", borderTop: "1px solid #eef2f6" }}>
                  <button className="btn btn-outline-secondary" onClick={() => setOpenModal(false)} disabled={loading}>
                    Cancel
                  </button>
                  <button
                    className={`btn ${hasActiveAssignment ? "btn-warning" : "btn-primary"} d-flex align-items-center gap-2`}
                    onClick={handleAssignedLeaveToUser}
                    disabled={loading || branchLeavePolicies.length === 0}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <FaIcons.FaCheckCircle />
                        {hasActiveAssignment ? "Reassign Balance" : "Assign Balance"}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {manageModal && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.45)", zIndex: 1040 }}
            onClick={() => setManageModal(false)}
          />
          <div className="modal fade show d-block" tabIndex={-1} style={{ zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered modal-lg" style={{ maxWidth: 960 }}>
              <div className="modal-content border-0 rounded-4 shadow overflow-hidden">
                <div className="modal-header" style={{ background: "#f7f8fa", borderBottom: "1px solid #eef2f6" }}>
                  <div>
                    <h5 className="modal-title fw-bold mb-0">Manage Leave Entitlements</h5>
                    <small className="text-muted">{selectedUser?.name} · {selectedUser?.branch_name}</small>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setManageModal(false)} />
                </div>

                <div className="modal-body p-4">
                  <div className="lp-manage-tabs">
                    <button
                      type="button"
                      className={`lp-manage-tab ${manageTab === "active" ? "active" : ""}`}
                      onClick={() => setManageTab("active")}
                    >
                      Current Active
                      <span className="tab-count">{activeEntitlements.length}</span>
                    </button>
                    <button
                      type="button"
                      className={`lp-manage-tab ${manageTab === "history" ? "active" : ""}`}
                      onClick={() => setManageTab("history")}
                    >
                      History
                      <span className="tab-count">{historyEntitlements.length}</span>
                    </button>
                  </div>

                  <div className="lp-section-card lp-table-card">
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead>
                          <tr>
                            <th>Leave Type</th>
                            <th className="text-center">Total</th>
                            <th className="text-center">Used</th>
                            <th className="text-center">Left</th>
                            <th className="text-center">Period</th>
                            <th className="text-center">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {manageModalSkeleton ? (
                            Array(3).fill(0).map((_, i) => <SkeletonRow key={i} />)
                          ) : manageTab === "active" ? (
                            activeEntitlements.length === 0 ? (
                              <tr>
                                <td colSpan="6">
                                  <div className="lp-empty-state py-3">
                                    <h6 className="fw-semibold">No active entitlements</h6>
                                    <p className="text-muted small mb-0">Use Assign to set up leave balance.</p>
                                  </div>
                                </td>
                              </tr>
                            ) : (
                              activeEntitlements.map((row) => renderEntitlementRow(row, false))
                            )
                          ) : historyEntitlements.length === 0 ? (
                            <tr>
                              <td colSpan="6">
                                <div className="lp-empty-state py-3">
                                  <h6 className="fw-semibold">No history yet</h6>
                                  <p className="text-muted small mb-0">
                                    Previous periods appear here after reassignment.
                                  </p>
                                </div>
                              </td>
                            </tr>
                          ) : (
                            historyGroups.map((group, groupIndex) => (
                              <React.Fragment key={`${group.period_start}-${group.period_end}-${groupIndex}`}>
                                <tr className="lp-history-period-row">
                                  <td colSpan="6">
                                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                                      <div>
                                        <span className="fw-semibold">
                                          {formatDate(group.period_start)} – {formatDate(group.period_end)}
                                        </span>
                                        <span className="text-muted ms-2 small">
                                          ({group.items.length} leave type{group.items.length !== 1 ? "s" : ""})
                                        </span>
                                      </div>
                                      {group.archived_at && (
                                        <span className="badge lp-badge-soft-secondary">
                                          Archived {formatDate(group.archived_at)}
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                                {group.items.map((row) => renderEntitlementRow(row, true))}
                              </React.Fragment>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="modal-footer" style={{ background: "#f7f8fa", borderTop: "1px solid #eef2f6" }}>
                  <button className="btn btn-outline-secondary" onClick={() => setManageModal(false)}>
                    Close
                  </button>
                  <button
                    className="lp-user-action-btn assign"
                    onClick={() => {
                      setManageModal(false);
                      openAssignModal(selectedUser);
                    }}
                  >
                    <FaIcons.FaPlusCircle />
                    {activeEntitlements.length > 0 ? "Reassign" : "Assign"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default LeaveAssignment;
